import moment from 'moment';

import { TranslateService } from '@ngx-translate/core';

import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';

import {
  BookingDiaryMonthDayModel,
  BookingDiaryMonthMechanicClickModel,
  BookingDiaryMonthMechanicModel
} from './typings/booking-diary.month';

import { BookingDiaryFoldedDaysModel } from './typings/booking-diary.view';
import { DATE_FORMAT }                 from './booking-diary.layout';

import {
  foldedDayLabel,
  isWeekendIndex
} from './booking-diary.weekend';

const BUSY_UTILISATION : number = 0.85;
// how many rings a cell draws before the rest go behind the expander
const MECHANIC_LIMIT   : number = 9;
const RING_RADIUS      : number = 13;
const RING_SIZE        : number = 30;
const SCROLL_END_ROWS  : number = 1;

// A month of days: totals and capacity per day, or a ring per mechanic, with more months on scroll
@Component({
  selector   : 'wor-booking-diary-month-grid',
  template: require('./booking-diary.month-grid.component.html')
})
export class BookingDiaryMonthGridComponent implements AfterViewChecked, OnChanges {
  @Input() foldedDays         : BookingDiaryFoldedDaysModel             = {};
  @Input() hasEarlier         : boolean                                 = false;
  @Input() isBusy             : boolean                                 = false;
  @Input() isGoldOrHigher     : boolean                                 = false;
  @Input() isShowingMechanics : boolean                                 = false;
  @Input() weekdayLabels      : Array<string>                           = [];
  @Input() weeks              : Array<Array<BookingDiaryMonthDayModel>> = [];

  @Output() dayClick       : EventEmitter<string>                              = new EventEmitter<string>();
  @Output() dayCreate      : EventEmitter<string>                              = new EventEmitter<string>();
  @Output() dayFoldToggled : EventEmitter<number>                              = new EventEmitter<number>();
  @Output() mechanicClick  : EventEmitter<BookingDiaryMonthMechanicClickModel> = new EventEmitter<BookingDiaryMonthMechanicClickModel>();
  @Output() monthShown     : EventEmitter<string>                              = new EventEmitter<string>();
  @Output() reachedEnd     : EventEmitter<void>                                = new EventEmitter<void>();
  @Output() reachedStart   : EventEmitter<void>                                = new EventEmitter<void>();

  @ViewChild('scroller', { static: true }) scroller : ElementRef<HTMLElement>;

  ringCircumference : number = 2 * Math.PI * RING_RADIUS;
  ringRadius        : number = RING_RADIUS;
  ringSize          : number = RING_SIZE;
  today             : string = moment().format(DATE_FORMAT);

  private expandedDays        : { [date : string] : boolean } = {};
  private heightBeforePrepend : number                        = 0;
  private lastScrollTop       : number                        = 0;
  private shownMonth          : string                        = '';

  constructor (
    private translateService : TranslateService
  ) {}

  ngOnChanges ( changes : SimpleChanges ) : void {
    this.expandedDays = {};

    // the diary may load again without the earlier month arriving; the scroll may then ask anew
    if ( changes.hasEarlier && this.hasEarlier && !changes.weeks ) {
      this.heightBeforePrepend = 0;
    }
  }

  // an earlier month grows the top of the scroll box, so the growth is put back and the view holds
  ngAfterViewChecked () : void {
    const element = this.scroller.nativeElement;

    if ( this.heightBeforePrepend && element.scrollHeight > this.heightBeforePrepend ) {
      element.scrollTop += element.scrollHeight - this.heightBeforePrepend;

      this.heightBeforePrepend = 0;
    }
  }

  create ( clickEvent : MouseEvent, day : BookingDiaryMonthDayModel ) : void {
    clickEvent.preventDefault();
    clickEvent.stopPropagation();

    if ( this.isPast(day) ) {
      return;
    }

    this.dayCreate.emit(day.date);
  }

  expandLabel ( day : BookingDiaryMonthDayModel ) : string {
    return this.isExpanded(day)
      ? this.translateService.instant('BOOKING_SPACE.DIARY.SHOW_FEWER')
      : this.translateService.instant('BOOKING_SPACE.DIARY.SHOW_MORE', { count: this.hiddenCount(day) });
  }

  fillClass ( day : BookingDiaryMonthDayModel ) : string {
    if ( this.isFull(day) ) {
      return 'full';
    }

    return this.utilisationOf(day) >= BUSY_UTILISATION
      ? 'busy'
      : '';
  }

  fillPercent ( day : BookingDiaryMonthDayModel ) : number {
    return this.utilisationOf(day) * 100;
  }

  // a rostered day shows its totals even with nothing booked; a day nobody works stays clear
  hasTotals ( day : BookingDiaryMonthDayModel ) : boolean {
    return day.isInMonth && !this.isShowingMechanics && ( day.jobs > 0 || day.availableHours > 0 );
  }

  hiddenCount ( day : BookingDiaryMonthDayModel ) : number {
    return Math.max(day.mechanics.length - MECHANIC_LIMIT, 0);
  }

  isExpanded ( day : BookingDiaryMonthDayModel ) : boolean {
    return !!this.expandedDays[day.date];
  }

  isFolded ( index : number ) : boolean {
    return !!this.foldedDays[index];
  }

  isFull ( day : BookingDiaryMonthDayModel ) : boolean {
    return day.jobs > 0 && day.availableHours <= 0;
  }

  // a day already gone cannot be booked into, so its add button is greyed rather than removed
  isPast ( day : BookingDiaryMonthDayModel ) : boolean {
    return day.date < this.today;
  }

  isWeekend ( index : number ) : boolean {
    return isWeekendIndex(index);
  }

  jobsLabel ( day : BookingDiaryMonthDayModel ) : string {
    return this.translateService.instant(day.jobs === 1
      ? 'BOOKING_SPACE.BOOKING'
      : 'JS_SPACE.DASHBOARD.LABEL.BOOKINGS');
  }

  loanCarTitle ( day : BookingDiaryMonthDayModel ) : string {
    return day.hasAvailableLoanCar
      ? this.translateService.instant('BOOKING_SPACE.DIARY.LOAN_CAR_AVAILABLE')
      : this.translateService.instant('BOOKING_SPACE.DIARY.LOAN_CAR_UNAVAILABLE');
  }

  mechanicTitle ( mechanic : BookingDiaryMonthMechanicModel ) : string {
    return [
      mechanic.name,
      this.utilisationLabel(mechanic.utilisation, mechanic.bookedHours, mechanic.availableHours)
    ]
      .filter(part => !!part)
      .join(' · ');
  }

  // the expander sits inside the clickable day cell, so its click must not also open the day
  onExpand ( clickEvent : MouseEvent, day : BookingDiaryMonthDayModel ) : void {
    clickEvent.preventDefault();
    clickEvent.stopPropagation();

    this.expandedDays[day.date] = !this.expandedDays[day.date];
  }

  // a ring already says how that mechanic's day is going, so it doubles as the way into it
  onMechanicClick (
    clickEvent : MouseEvent,
    day        : BookingDiaryMonthDayModel,
    mechanic   : BookingDiaryMonthMechanicModel
  ) : void {
    clickEvent.preventDefault();
    clickEvent.stopPropagation();

    this.mechanicClick.emit({
      date      : day.date,
      mechanicId: mechanic.id
    });
  }

  // near the bottom the next month is asked for; scrolling up near the top, the month before
  onScroll ( scrollEvent : Event ) : void {
    const element    = scrollEvent.target as HTMLElement;
    const rowGap     = element.scrollHeight / Math.max(this.weeks.length, 1) * SCROLL_END_ROWS;
    const isUpwards  = element.scrollTop < this.lastScrollTop;

    this.lastScrollTop = element.scrollTop;

    if ( element.scrollTop + element.clientHeight >= element.scrollHeight - rowGap ) {
      this.reachedEnd.emit();
    }

    if ( this.hasEarlier && isUpwards && !this.heightBeforePrepend && element.scrollTop <= rowGap ) {
      this.askForEarlier(element);
    }

    this.sayWhichMonth(element, rowGap);
  }

  // at the very top no scroll event fires, so the wheel itself asks for the month before
  onWheel ( wheelEvent : WheelEvent ) : void {
    const element = this.scroller.nativeElement;

    if ( wheelEvent.deltaY < 0 && element.scrollTop === 0 && this.hasEarlier && !this.heightBeforePrepend ) {
      this.askForEarlier(element);
    }
  }

  open ( day : BookingDiaryMonthDayModel ) : void {
    if ( day.isInMonth ) {
      this.dayClick.emit(day.date);
    }
  }

  requestTitle ( day : BookingDiaryMonthDayModel ) : string {
    return day.requests === 1
      ? this.translateService.instant('BOOKING_SPACE.DIARY.REQUEST_FOR_DAY')
      : this.translateService.instant('BOOKING_SPACE.DIARY.REQUESTS_FOR_DAY', { count: day.requests });
  }

  // the arc of the booked share, drawn over a track of the full circumference
  ringDash ( mechanic : BookingDiaryMonthMechanicModel ) : string {
    return `${ mechanic.utilisation * this.ringCircumference } ${ this.ringCircumference }`;
  }

  trackDay ( index : number, day : BookingDiaryMonthDayModel ) : string {
    return day.date;
  }

  trackMechanic ( index : number, mechanic : BookingDiaryMonthMechanicModel ) : string {
    return mechanic.id;
  }

  trackWeek ( index : number, week : Array<BookingDiaryMonthDayModel> ) : string {
    return week[0].date;
  }

  unassignedTitle ( day : BookingDiaryMonthDayModel ) : string {
    return this.translateService.instant('BOOKING_SPACE.DIARY.UNASSIGNED_ON_DAY', { count: day.unassigned });
  }

  utilisationTitle ( day : BookingDiaryMonthDayModel ) : string {
    if ( !day.isInMonth || !day.jobs ) {
      return '';
    }

    return this.utilisationLabel(this.utilisationOf(day), day.bookedHours, day.availableHours);
  }

  visibleMechanics ( day : BookingDiaryMonthDayModel ) : Array<BookingDiaryMonthMechanicModel> {
    return this.isExpanded(day)
      ? day.mechanics
      : day.mechanics.slice(0, MECHANIC_LIMIT);
  }

  weekdayLabel ( index : number, label : string ) : string {
    return this.isFolded(index)
      ? foldedDayLabel(label)
      : label;
  }

  private askForEarlier ( element : HTMLElement ) : void {
    this.heightBeforePrepend = element.scrollHeight;

    this.reachedStart.emit();
  }

  // the week at the top of the box names the month in view, by the day in the middle of it
  private sayWhichMonth ( element : HTMLElement, rowHeight : number ) : void {
    const index = Math.min(Math.floor(element.scrollTop / Math.max(rowHeight, 1)), this.weeks.length - 1);
    const week  = this.weeks[index];

    if ( !week ) {
      return;
    }

    const month = week[3].date.slice(0, 7) + '-01';

    if ( month !== this.shownMonth ) {
      this.shownMonth = month;

      this.monthShown.emit(month);
    }
  }

  private utilisationLabel ( utilisation : number, bookedHours : number, availableHours : number ) : string {
    return this.translateService.instant('BOOKING_SPACE.DIARY.UTILISATION', {
      booked : bookedHours,
      free   : availableHours,
      percent: Math.round(utilisation * 100)
    });
  }

  private utilisationOf ( day : BookingDiaryMonthDayModel ) : number {
    const capacity = day.bookedHours + day.availableHours;

    return capacity
      ? day.bookedHours / capacity
      : ( day.jobs > 0 ? 1 : 0 );
  }
}
